import {makeObservable, computed, observable, override} from 'mobx';

import {v4 as uuid} from 'uuid';

import {IPromiseWithExtendedError} from '@kouch.tv/brandis-api';
import {ApplicationChildModel} from '@kouch.tv/brandis-application';
import {TwitchModel} from '@kouch.tv/twitch';

import {COOKIE} from './constants';

export interface IPlatformData {
	sessionId: string;
	viewerCookieId: string;
	controlPanelAccessLink: string;
}

export interface IPlatformAnalyticsContext {
	product: 'brandis';
	platform: 'twitch';

	userId: string; // Required name for analytics system
	anonId: string; // Required name for analytics system

	clientId: string;
	sessionId: string;

	viewerId: string;
	viewerTrustedId: string;
	viewerOpaqueId: string;
	viewerCookieId: string;
	viewerName: string;
	viewerRole: string;
	viewerStatus: 'trusted' | 'authorized' | 'unauthorized' | 'unknown';
	viewerIsTrusted: boolean;
	viewerIsFollower: boolean;
	viewerIsSubscriber: boolean;
	viewerIsFullscreen: boolean;
	viewerTheme: 'light' | 'dark' | '';
	viewerBitrate: number;
	viewerBuffer: number;
	viewerLatency: number;
	viewerBroadcastWidth: number;
	viewerBroadcastHeight: number;
	viewerDisplayWidth: number;
	viewerDisplayHeight: number;

	channelId: string;
	channelName: string;

	streamId: string;
	streamTitle: string;
	streamLanguage: string;
	streamDelay: number;
	streamViewersCount: number;
	streamTagIds: string;
	streamGameId: string;
	streamGameName: string;
	streamIsMature: boolean;
}

export class PlatformModel extends ApplicationChildModel<IPlatformData> {
	@override
	protected $data: IPlatformData = {
		sessionId: '',
		viewerCookieId: '',
		controlPanelAccessLink: '',
	};

	@observable
	public readonly twitch: TwitchModel = new TwitchModel();

	@computed
	public get analyticsContext(): IPlatformAnalyticsContext {
		return {
			product: 'brandis',
			platform: 'twitch',

			userId: this.twitch.user.idTrusted,
			anonId: this.twitch.user.idOpaque,

			clientId: this.twitch.api.clientId,
			sessionId: this.getData('sessionId', ''),

			viewerId: this.twitch.user.id,
			viewerTrustedId: this.twitch.user.idTrusted,
			viewerOpaqueId: this.twitch.user.idOpaque,
			viewerCookieId: this.getData('viewerCookieId'),
			viewerName: this.twitch.user.name,
			viewerRole: this.twitch.user.role,
			viewerStatus: ((user) => {
				if (user.idOpaque.startsWith('U')) {
					if (user.isTrusted) {
						return 'trusted';
					}

					return 'authorized';
				}

				if (user.idOpaque.startsWith('A')) {
					return 'unauthorized';
				}

				return 'unknown';
			})(this.twitch.user),
			viewerIsTrusted: this.twitch.user.isTrusted,
			viewerIsFollower: this.twitch.user.isFollower,
			viewerIsSubscriber: this.twitch.user.isSubscriber,
			viewerIsFullscreen: this.twitch.context.isFullscreen,
			viewerTheme: this.twitch.context.theme,
			viewerBitrate: this.twitch.context.bitrate,
			viewerBuffer: this.twitch.context.buffer,
			viewerLatency: this.twitch.context.latency,
			viewerBroadcastWidth: this.twitch.context.video.width,
			viewerBroadcastHeight: this.twitch.context.video.height,
			viewerDisplayWidth: this.twitch.context.display.width,
			viewerDisplayHeight: this.twitch.context.display.height,

			channelId: this.twitch.channel.id,
			channelName: this.twitch.channel.name,

			streamId: this.twitch.channel.streamId,
			streamTitle: this.twitch.channel.streamTitle,
			streamLanguage: this.twitch.channel.streamLanguage,
			streamDelay: this.twitch.channel.streamDelay,
			streamViewersCount: this.twitch.channel.streamViewersCount,
			streamTagIds: this.twitch.channel.streamTagIds.join('|'),
			streamGameId: this.twitch.channel.gameId,
			streamGameName: this.twitch.channel.gameName,
			streamIsMature: this.twitch.channel.isStreamMature,
		};
	}

	constructor() {
		super();
		makeObservable(this);
	}

	@override
	public initialize(): this {
		this._resolveSession();
		this._resolveViewer();

		super.initialize();

		return this;
	}

	@override
	public destroy(): void {
		super.destroy();
	}

	private _resolveSession(): void {
		const environment = this.application.getModel('environment');

		let sessionId = environment.getSessionData('sessionId');

		if (!sessionId) {
			sessionId = uuid();
			environment.setSessionData('sessionId', sessionId);
		}

		this.updateData('sessionId', sessionId);
	}

	private _resolveViewer(): void {
		const environment = this.application.getModel('environment');

		let viewerCookieId = environment.getCookie(COOKIE.X_VIEWER_KEY) || environment.getStorageData(COOKIE.X_VIEWER_KEY);

		if (!viewerCookieId) {
			viewerCookieId = uuid();

			try {
				environment.setCookie(COOKIE.X_VIEWER_KEY, viewerCookieId, COOKIE.X_VIEWER_OPTIONS);
			}
			catch (error) {
				environment.setStorageData(COOKIE.X_VIEWER_KEY, viewerCookieId);
			}
		}

		this.updateData('viewerCookieId', viewerCookieId);
	}

	public getControlPanelAccessLink(): IPromiseWithExtendedError<string> {
		return (
			this.application.api.influencer.getAccessLink({
				token: this.twitch.user.tokenPrimary,
				clientId: this.twitch.api.clientId,
			})
				.catch((error) => {
					this.updateData('controlPanelAccessLink', '');

					throw error;
				})
				.then((controlPanelAccessLink) => {
					this.updateData('controlPanelAccessLink', controlPanelAccessLink);

					return controlPanelAccessLink;
				})

		);
	}
}
